import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { Header } from '../components/lobby/Header.js';
import { ActionCard } from '../components/lobby/ActionCard.js';
import { UtilityCard } from '../components/lobby/UtilityCard.js';
import { Footer } from '../components/lobby/Footer.js';
import { useRoomStore } from '../store/room.store.js';
import { useAudioStore } from '../store/audio.store.js';

export function HomeScreen() {
  const { user, logout, isSessionChecked } = useAuthStore();
  const navigate = useNavigate();

  const [rejoinRoomCode, setRejoinRoomCode] = useState<string | null>(null);

  const { playMusic } = useAudioStore();

  useEffect(() => {
    playMusic();
  }, [playMusic]);

  useEffect(() => {
    const code = localStorage.getItem('active_room_code');
    if (code) {
      setRejoinRoomCode(code);
    }
  }, []);

  const handleRejoin = async () => {
    if (rejoinRoomCode && user) {
      const { joinRoom } = useRoomStore.getState();
      await joinRoom(rejoinRoomCode, user.displayName);
      navigate('/waiting-room');
    }
  };

  // Route protection - redirect to login if no active user session
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/login');
    }
  };

  const handleOpenSettings = () => {
    navigate('/settings');
  };

  const handleOpenNotifications = () => {
    alert('No new notifications.');
  };

  const handleAction = (route: string) => {
    navigate(route);
  };

  // Stagger child animation variants
  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const utilityStripVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        delay: 0.4,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden select-none">
      {/* 3D background lighting & bingo balls */}
      <BackgroundEffects />

      {/* Header bar */}
      <Header
        user={user}
        onOpenSettings={handleOpenSettings}
        onLogout={handleLogout}
      />

      {/* Rejoin Banner */}
      {rejoinRoomCode && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="z-40 w-full max-w-lg mx-auto mt-2 px-4 pointer-events-auto"
        >
          <div className="glass border border-purple-500/30 rounded-2xl p-3 flex items-center justify-between shadow-glow bg-purple-950/20 backdrop-blur-md">
            <div className="flex items-center space-x-3 text-left">
              <span className="text-xl animate-pulse">🎮</span>
              <div>
                <span className="block text-[8px] font-black uppercase tracking-wider text-purple-400">
                  Game in Progress
                </span>
                <p className="text-[10px] text-white font-bold">
                  You have an active match in room <span className="text-amber-400 font-mono font-black">#{rejoinRoomCode}</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleRejoin}
              className="px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-[10px] font-black uppercase tracking-widest text-white cursor-pointer shadow-glow-accent"
            >
              Rejoin
            </button>
          </div>
        </motion.div>
      )}

      {/* Main Action area grid */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 max-h-[calc(100vh-140px)] z-20 overflow-y-auto">
        <motion.div
          variants={gridVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl w-full"
        >
          {/* Create Room Card */}
          <ActionCard
            title="Create Room"
            description="Create your own multiplayer room."
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            }
            onClick={() => handleAction('/create-room')}
          />

          {/* Join Room Card */}
          <ActionCard
            title="Join Room"
            description="Join using a room code."
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              </svg>
            }
            onClick={() => handleAction('/join-room')}
          />

          {/* Active Lobbies Card */}
          <ActionCard
            title="Active Rooms"
            description="Browse and join open game lobbies."
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
            onClick={() => handleAction('/rooms')}
          />
        </motion.div>

        {/* Bottom Utility Card bar strip */}
        <motion.div
          variants={utilityStripVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap items-center justify-center gap-3 mt-6"
        >
          <UtilityCard
            label="Friends"
            icon="👥"
            onClick={() => {}}
            isComingSoon={true}
          />
          <UtilityCard
            label="Help"
            icon="❓"
            onClick={() => handleAction('/help')}
          />
        </motion.div>
      </main>

      {/* Footer bar */}
      <Footer />
    </div>
  );
}
